import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import PropTypes from "prop-types";

import PortfolioTab from "./Tabs/PortfolioTab";
import LegalAddressTab from "./Tabs/LegalAddressTab";
import DocumentsTab from "./Tabs/DocumentsTab";
import ProfileTab from "./Tabs/ProfileTab";
import ArchivesTab from "./Tabs/ArchivesTab";
import ShareHolderTab from "./Tabs/ShareHolderTab";
import RelatedPeopleTab from "./Tabs/RelatedPeopleTab";
import PhonesTab from "./Tabs/PhonesTab";
import BankAccountsTab from "./Tabs/BankAccountsTab";
import QuestionsTab from "./Tabs/QuestionsTab";
import ContractsTab from "./Tabs/ContractsTab";
import ExtendedDataTab from "./Tabs/ExtendedDataTab";
import { PersonType } from "./types";
import { PersonMode, societyTypes } from "../../../utils/Helper";
import FiscalResidencesTab from "./Tabs/FiscalResidencesTab";
import FinalizeTab from "./Tabs/FinalizeTab";
import KYCTab from "./Tabs/KYCTab";
import CircularProgress from "../../CircularProgress/CircularProgress";

// Canonical UI tab names, matching the TabName values returned by GetUiTabsPermissions
// (based on the tab component names, e.g. DocumentsTab -> "Documents")
export const PersonTabName = {
  Profile: "Profile",
  Documents: "Documents",
  Archives: "Archives",
  Phones: "Phones",
  FiscalResidences: "FiscalResidences",
  BankAccounts: "BankAccounts",
  Questions: "Questions",
  KYC: "KYC",
  LegalAddress: "LegalAddress",
  Contracts: "Contracts",
  ExtendedData: "ExtendedData",
  Finalize: "Finalize",
  Portfolio: "Portfolio",
  ShareHolder: "ShareHolder",
  RelatedPeople: "RelatedPeople",
};

const PersonTabs = ({
  person,
  setPersonData,
  setPerson,
  professions,
  activitySectors,
  publicPositions,
  personLinks,
  documentTypes,
  entityTypes,
  subAssetClasses,
  societyTypes,
  companyLinks,
  patrimonies,
  archivesTypes,
  refresh,
  phoneTypes,
  handleRedirectAccount,
  handleRedirectPerson,
  setRedirectPerson,
  services,
  role,
  mode,
  setMode,
  onboarding,
  Link,
  holder,
  isPerson,
  banks,
  currencies,
  fetchGetAssetInformation,
  needsExtraFields,
  riskByPersonIsEnabled,
  riskByPersonIsZero,
  viewDigitalRelatedFrom,
  userSession,
  opsViewAccountName,
  newOnboardingVersion,
  getPhones,
  getBankAccounts,
  isKYCRiskUpdateEnabled,
  kycSource,
  kycChangesCount,
  kycChangesData,
  kycChangesFetchs,
  kycAlertEventId,
  kycCurrentUser,
  readOnlyTabs = [],
}) => {
  const [tab, setTab] = useState(0);

  const isTabReadOnly = (tabName) => {
    if (!readOnlyTabs || !readOnlyTabs.length) {
      return false;
    }
    const match = readOnlyTabs.find((t) => t.TabName === tabName);
    return match ? !!match.TabIsReadOnly : false;
  };

  useEffect(() => {

    const currentUrl = window.location.href;

    if (currentUrl.includes("tab=kyc")) {
      setTab(11);
    }
  }, []);

  const toggleTab = (newTab) => {
    if (mode === PersonMode.New && onboarding && person.PersonId !== "" && person.PersonId !== "0") {
      setTab(newTab);
    }
    if (!onboarding && person.PersonId !== "" && person.PersonId !== "0") {
      setTab(newTab);
    }
  };

  if (!person) {
    return <CircularProgress />;
  }

  return (
    <Card>
      <CardHeader>
        <Nav tabs>
          <NavItem>
            <NavLink className={classnames({ active: tab === 0 })} onClick={() => toggleTab(0)}>
              <i className="fa fa-user" /> Profile
            </NavLink>
          </NavItem>
          {person.PersonType === PersonType.Physical ? (
            <NavItem>
              <NavLink className={classnames({ active: tab === 1 })} onClick={() => toggleTab(1)}>
                <i className="fa fa-folder" /> Person ID
              </NavLink>
            </NavItem>
          ) : (
            <NavItem>
              <NavLink className={classnames({ active: tab === 5 })} onClick={() => toggleTab(5)}>
                <i className="fa fa-users" /> Share Holders
              </NavLink>
            </NavItem>
          )}
          {person.PersonType !== PersonType.Physical && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 13 })} onClick={() => toggleTab(13)}>
                <i className="fa fa-users" /> Related People
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 2 })} onClick={() => toggleTab(2)}>
                <i className="fa fa-file" /> Archives
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 3 })} onClick={() => toggleTab(3)}>
                <i className="fa fa-phone" /> Phones
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 6 })} onClick={() => toggleTab(6)}>
                <i class="fa fa-globe" aria-hidden="true"></i> Fiscal Residences
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && !riskByPersonIsZero && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 7 })} onClick={() => toggleTab(7)}>
                <i class="fa fa-university" aria-hidden="true"></i> Bank Accounts
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && riskByPersonIsEnabled && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 8 })} onClick={() => toggleTab(8)}>
                <i class="fa fa-list" aria-hidden="true"></i> Suitability
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 11 })} onClick={() => toggleTab(11)}>
                <i class="fa fa-search" aria-hidden="true"></i> KYC
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 12 })} onClick={() => toggleTab(12)}>
                <i class="fa fa-map-marker" aria-hidden="true"></i> Legal Address
              </NavLink>
            </NavItem>
          )}
          {!person.PersonIsMinimal && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 9 })} onClick={() => toggleTab(9)}>
                <i class="fa fa-book" aria-hidden="true"></i> Contracts
              </NavLink>
            </NavItem>
          )}
          {
            <NavItem>
              <NavLink className={classnames({ active: tab === 14 })} onClick={() => toggleTab(14)}>
                <i class="fa fa-external-link-square" aria-hidden="true"></i> Extended Data
              </NavLink>
            </NavItem>
          }

          {(mode === PersonMode.New || newOnboardingVersion) && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 10 })} onClick={() => toggleTab(10)}>
                <i class="fa fa-check-square-o" aria-hidden="true"></i> Finalize
              </NavLink>
            </NavItem>
          )}

          {mode !== PersonMode.New && !newOnboardingVersion && (
            <NavItem>
              <NavLink className={classnames({ active: tab === 4 })} onClick={() => toggleTab(4)}>
                <i className="fa fa-pie-chart" /> Account Portfolios
              </NavLink>
            </NavItem>
          )}
        </Nav>
      </CardHeader>
      <CardBody>
        {tab === 0 && (
          <ProfileTab
            manual={person.PersonUpdateManual === "1" || person.PersonUpdateManual ? true : false}
            person={person}
            mode={mode}
            documents={person.Documents}
            documentTypes={documentTypes}
            societyTypes={societyTypes}
            referesh={refresh}
            handleRedirectPerson={handleRedirectPerson}
            {...{
              setPersonData,
              professions,
              activitySectors,
              publicPositions,
              personLinks,
              documentTypes,
              companyLinks,
              patrimonies,
              services,
              entityTypes,
              subAssetClasses,
              societyTypes,
              role,
              mode,
              setMode,
              onboarding,
              holder,
              isPerson,
              refresh,
              needsExtraFields,
              viewDigitalRelatedFrom,
              userSession,
              newOnboardingVersion,
              currencies,
            }}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Profile)}
          />
        )}
        {tab === 1 && (
          <DocumentsTab
            person={person}
            documents={person.Documents}
            documentTypes={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Documents)}
          />
        )}
        {tab === 2 && (
          <ArchivesTab
            person={person}
            archives={
              person.Archives &&
              person.Archives.filter((a) => a.PersonArchiveSubTypeInternalName !== "Contract")
            }
            archivesTypes={archivesTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            refresh={refresh}
            onboarding={onboarding}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Archives)}
          />
        )}
        {tab === 3 && (
          <PhonesTab
            person={person}
            phones={person.Phone}
            phoneTypes={phoneTypes}
            setPersonData={setPersonData}
            services={services}
            role={role}
            mode={mode}
            onboarding={onboarding}
            Link={Link}
            userSession={userSession}
            getPhones={getPhones}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Phones)}
          />
        )}
        {tab === 6 && (
          <FiscalResidencesTab
            person={person}
            document={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            fiscalresidences={person.PersonFiscalResidence}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.FiscalResidences)}
          />
        )}
        {tab === 4 && (
          <PortfolioTab
            person={person}
            portfolio={person.Portfolio}
            handleRedirectAccount={handleRedirectAccount}
            services={services}
            mode={mode}
            onboarding={onboarding}
            opsViewAccountName={role="BackOffice" ? opsViewAccountName: true}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Portfolio)}
          />
        )}
        {tab === 5 && (
          <ShareHolderTab
            holder={holder}
            person={person}
            setPersonData={setPersonData}
            services={services}
            shareHolders={person.ShareHolder}
            mode={mode}
            onboarding={onboarding}
            Link={Link}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.ShareHolder)}
          />
        )}
        {tab === 13 && (
          <RelatedPeopleTab
            holder={holder}
            person={person}
            setPersonData={setPersonData}
            services={services}
            shareHolders={person.ShareHolder}
            mode={mode}
            onboarding={onboarding}
            Link={Link}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.RelatedPeople)}
          />
        )}
        {tab === 7 && (
          <BankAccountsTab
            person={person}
            documents={person.Documents}
            documentTypes={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            banks={banks}
            currencies={currencies}
            bankAccounts={person.PersonBankAccount}
            userSession={userSession}
            newOnboardingVersion={newOnboardingVersion}
            getBankAccounts={getBankAccounts}
            tabIsReadOnly={isTabReadOnly(PersonTabName.BankAccounts)}
          />
        )}
        {tab === 8 && (
          <QuestionsTab
            person={person}
            documents={person.Documents}
            documentTypes={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            questions={person.PersonQuestion}
            refresh={refresh}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Questions)}
          />
        )}
        {tab === 9 && (
          <ContractsTab
            person={person}
            archives={
              person.Archives &&
              person.Archives.filter((a) => a.PersonArchiveSubTypeInternalName === "Contract")
            }
            archivesTypes={archivesTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            refresh={refresh}
            onboarding={onboarding}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Contracts)}
          />
        )}
        {tab === 11 && (
          <KYCTab
            person={person}
            documents={person.Documents}
            documentTypes={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            KYC={person.KYC}
            role={role}
            refresh={refresh}
            isKYCRiskUpdateEnabled={isKYCRiskUpdateEnabled}
            kycSource={kycSource}
            changesCount={kycChangesCount}
            changesData={kycChangesData}
            changesFetchs={kycChangesFetchs}
            alertEventId={kycAlertEventId}
            currentUser={kycCurrentUser}
            tabIsReadOnly={isTabReadOnly(PersonTabName.KYC)}
            isKYCRiskUpdateEnabled={isKYCRiskUpdateEnabled}
          />
        )}

        {tab === 14 && (
          <ExtendedDataTab
            person={person}
            role={role}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            refresh={refresh}
            userSession={userSession}
            newOnboardingVersion={newOnboardingVersion}
            tabIsReadOnly={isTabReadOnly(PersonTabName.ExtendedData)}
          />
        )}
        {tab === 10 && (
          <FinalizeTab
            person={person}
            documents={person.Documents}
            documentTypes={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            setRedirectPerson={setRedirectPerson}
            riskByPersonIsEnabled={riskByPersonIsEnabled}
            newOnboardingVersion={newOnboardingVersion}
            tabIsReadOnly={isTabReadOnly(PersonTabName.Finalize)}
          />
        )}
        {tab === 12 && (
          <LegalAddressTab
            person={person}
            legalAddress={person.LegalAddress}
            documents={person.Documents}
            documentTypes={documentTypes}
            setPersonData={setPersonData}
            services={services}
            mode={mode}
            onboarding={onboarding}
            setRedirectPerson={setRedirectPerson}
            refresh={refresh}
            userSession={userSession}
            tabIsReadOnly={isTabReadOnly(PersonTabName.LegalAddress)}
          />
        )}
      </CardBody>
    </Card>
  );
};

PersonTabs.propTypes = {
  person: PropTypes.object.isRequired,
  setPersonData: PropTypes.func.isRequired,
  professions: PropTypes.array,
  publicPositions: PropTypes.array,
  personLinks: PropTypes.array,
  documentTypes: PropTypes.array,
  companyLinks: PropTypes.array,
  patrimonies: PropTypes.array,
  archivesTypes: PropTypes.array,
  handleRedirectAccount: PropTypes.func.isRequired,
  services: PropTypes.object.isRequired,
  entityTypes: PropTypes.array,
  role: PropTypes.string,
  readOnlyTabs: PropTypes.array,
};

PersonTabs.defaultProps = {
  professions: undefined,
  publicPositions: undefined,
  personLinks: undefined,
  documentTypes: undefined,
  companyLinks: undefined,
  patrimonies: undefined,
  archivesTypes: undefined,
  entityTypes: undefined,
  readOnlyTabs: [],
};

export default PersonTabs;
